import { Link, useLocation, useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ModalContainer from '../../components/modalContainer';

import './style.scss';
import {
  getBeneficiaries,
  getBeneficiariesBySchool,
  getList,
  getSchools
} from '../../API/apiService';
import AddBeneficiaryModal from '../../components/addBeneficiaryModal';
import UserCard from '../../components/userCard';
import DownloadBeneficiaries from '../../components/downloadBeneficiaries';
import ProjectLinkModal from '../../components/adminSchools/projectLinkModal';
import { constants } from '../../config/constants';
import SocialControlGroup from './socialControlGroup';

function normalizeGroupName(group) {
  if (!group?.code) return '';
  return group.code.indexOf('Grupo') !== -1 ? group.code : `Grupo ${group.code}`;
}

function resolveAdvisorName(school) {
  return school?.name ?? school?.address ?? school?.code ?? 'Asesoria';
}

function StandardGroupView() {
  const location = useLocation();
  const { groupParam, schoolParam } = useParams();
  const receivedSchool = location.state?.school;
  const receivedGroup = location.state?.group;
  const isAdvisorContext = Boolean(schoolParam && !groupParam);
  const currentBackTo = useMemo(() => ({
    pathname: location.pathname,
    state: location.state
  }), [location.pathname, location.state]);

  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [openProjectLinkModal, setOpenProjectLinkModal] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [genders, setGenders] = useState([]);
  const [projectGroup, setProjectGroup] = useState(receivedGroup || null);
  const [advisorSchool, setAdvisorSchool] = useState(receivedSchool || null);

  const contextSchoolId =
    receivedSchool?.id ?? advisorSchool?.id ?? schoolParam ?? null;
  const contextGroupId =
    !isAdvisorContext
      ? receivedGroup?.id ?? groupParam ?? null
      : null;
  const info = useMemo(() => ({
    school: contextSchoolId,
    group: contextGroupId
  }), [contextSchoolId, contextGroupId]);

  const loadBeneficiaries = useCallback(async (contextId) => {
    const targetId = contextId || (isAdvisorContext ? contextSchoolId : contextGroupId);

    if (!targetId) {
      setBeneficiaries([]);
      return;
    }

    const list = isAdvisorContext
      ? await getBeneficiariesBySchool(targetId)
      : await getBeneficiaries(targetId);

    setBeneficiaries(Array.isArray(list) ? list : []);
  }, [contextGroupId, contextSchoolId, isAdvisorContext]);

  const loadGenders = useCallback(async () => {
    const list = await getList('gender');
    if (list) {
      setGenders(list);
    }
  }, []);

  useEffect(() => {
    if (!isAdvisorContext) return;

    if (receivedSchool) {
      setAdvisorSchool(receivedSchool);
      return;
    }

    let isMounted = true;

    async function loadAdvisorSchool() {
      const currentUserId = localStorage.getItem(constants.USER_ID);
      const schoolsResponse = await getSchools(
        currentUserId && currentUserId !== 'null' ? Number(currentUserId) : null
      );
      const schools = Array.isArray(schoolsResponse)
        ? schoolsResponse
        : Array.isArray(schoolsResponse?.data)
          ? schoolsResponse.data
          : [];
      const foundSchool = schools.find(
        school => Number(school?.id) === Number(schoolParam)
      );

      if (!isMounted) return;

      setAdvisorSchool(foundSchool || { id: Number(schoolParam) });
    }

    loadAdvisorSchool();

    return () => {
      isMounted = false;
    };
  }, [isAdvisorContext, receivedSchool, schoolParam]);

  useEffect(() => {
    if (isAdvisorContext) return;

    if (receivedGroup) {
      setProjectGroup(receivedGroup);
      setGroupName(normalizeGroupName(receivedGroup));
      if (receivedGroup?.id) {
        loadBeneficiaries(receivedGroup.id);
      }
      return;
    }

    if (groupParam) {
      loadBeneficiaries(groupParam);
    }
  }, [isAdvisorContext, receivedGroup, groupParam, loadBeneficiaries]);

  useEffect(() => {
    if (!isAdvisorContext || !contextSchoolId) return;
    loadBeneficiaries(contextSchoolId);
  }, [isAdvisorContext, contextSchoolId, loadBeneficiaries]);

  useEffect(() => {
    loadGenders();
  }, [loadGenders]);

  const activeBeneficiaries = Array.isArray(beneficiaries)
    ? beneficiaries.filter(b => b?.active !== false)
    : [];
  const rawGroupCode = receivedGroup?.code || groupName || contextGroupId;
  const safeGroupCode = rawGroupCode
    ? rawGroupCode.toString().replace(/\s+/g, '_')
    : 'grupo';
  const fileName = `beneficiarios_${safeGroupCode}.xlsx`;
  const currentProjectGroup =
    projectGroup ||
    (contextGroupId
      ? {
          id: contextGroupId,
          code: rawGroupCode || groupName
        }
      : null);
  const advisorTitle = advisorSchool?.code || receivedSchool?.code || 'Asesoria';
  const advisorValue = resolveAdvisorName(advisorSchool || receivedSchool);

  function handleBeneficiaryDeleted(deletedId) {
    setBeneficiaries(currentList =>
      currentList.filter(beneficiary => beneficiary?.id !== deletedId)
    );
    loadBeneficiaries();
  }

  return (
    <>
      {openModalAdd && (
        <ModalContainer>
          <AddBeneficiaryModal
            info={info}
            setOpenModalAdd={setOpenModalAdd}
            updateData={loadBeneficiaries}
            backTo={currentBackTo}
          />
        </ModalContainer>
      )}
      {openProjectLinkModal && currentProjectGroup?.id && !isAdvisorContext && (
        <ModalContainer>
          <ProjectLinkModal
            group={currentProjectGroup}
            onClose={() => setOpenProjectLinkModal(false)}
            onSaved={updatedGroup => setProjectGroup(updatedGroup)}
          />
        </ModalContainer>
      )}
      <section className='beneficiaries'>
        <div
          className={`beneficiaries__main group${
            isAdvisorContext ? ' beneficiaries__main--advisor' : ''
          }`}
        >
          <div className='group__info info'>
            <div className='info__icon icon'>
              <span className='material-symbols-rounded icon__img'>school</span>
            </div>
            <span className='info__title'>
              {isAdvisorContext ? advisorTitle : receivedSchool?.code}
            </span>
            <span className='info__value'>
              {isAdvisorContext ? advisorValue : groupName}
            </span>
          </div>

          {isAdvisorContext ? (
            <Link
              className='group__btn btn'
              to={`/escuelas/asesoria/${contextSchoolId}/evidencias`}
              state={{
                school: advisorSchool || receivedSchool || { id: contextSchoolId },
                backTo: currentBackTo
              }}
            >
              <span className='material-symbols-rounded btn__icon'>
                folder_open
              </span>
              <span className='btn__text'>Evidencias</span>
            </Link>
          ) : (
            <Link
              className='group__btn btn'
              to={'/escuelas/grupo/lista_de_encuentros'}
              state={{
                group: receivedGroup || contextGroupId,
                school: receivedSchool || contextSchoolId,
                backTo: currentBackTo
              }}>
              <span className='material-symbols-rounded btn__icon'>
                grid_view
              </span>
              <span className='btn__text'>Encuentros</span>
            </Link>
          )}

          {!isAdvisorContext && (
            <button
              className='group__btn btn'
              type='button'
              onClick={() => setOpenProjectLinkModal(true)}
              disabled={!currentProjectGroup?.id}
            >
              <span className='material-symbols-rounded btn__icon'>link</span>
              <span className='btn__text'>Link proyecto</span>
            </button>
          )}

          <button
            className='group__btn btn'
            type='button'
            onClick={() => setOpenModalAdd(true)}
          >
            <span className='material-symbols-rounded btn__icon'>person_add</span>
            <span className='btn__text'>
              Agregar Beneficiario
            </span>
          </button>

          {!isAdvisorContext && (
            <DownloadBeneficiaries
              beneficiaries={activeBeneficiaries}
              genders={genders}
              buttonText='Descargar beneficiarios'
              buttonIcon='download'
              buttonClassName='group__btn btn group__btn--download'
              containerClassName='group__download'
              fileName={fileName}
            />
          )}
        </div>

        <div className='beneficiaries__students'>
          {beneficiaries.length > 0 ? (
            beneficiaries.map((user, index) => (
              <UserCard
                key={user?.id || index}
                user={user}
                backTo={currentBackTo}
                onDeleted={handleBeneficiaryDeleted}
              />
            ))
          ) : (
            <p className='beneficiaries__empty'>
              Aun no hay beneficiarios registrados.
            </p>
          )}
        </div>
      </section>
    </>
  );
}

function UserListGroup() {
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const initialActivity = location.state?.activity || null;
  const initialGroup = location.state?.activityGroup || null;
  const activityId =
    searchParams.get('activityId') ?? initialActivity?.idActivity ?? null;
  const groupId =
    searchParams.get('activityGroupId') ?? initialGroup?.id ?? null;
  const isSocialControlContext = Boolean(
    location.state?.controlSocialContext ||
      (Number(activityId) > 0 && Number(groupId) > 0)
  );

  if (isSocialControlContext) {
    return (
      <SocialControlGroup
        activityId={activityId}
        groupId={groupId}
        initialActivity={initialActivity}
        initialGroup={initialGroup}
      />
    );
  }

  return <StandardGroupView />;
}

export default UserListGroup;
